import React from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';
import { erpDriverTypes } from '../../data/erpDriverTypes';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ErpAgentActions from '../../actions/erpAgentActions';
import * as UiActions from '../../actions/uiActions';

import classes from './NewAgentModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    erpDriverType: Yup.string()
        .required()
});

const NewErpAgentModal = ({
    close,
    open,
    title,
    isLoading,
    toggleCredentialsModal,
    toggleLoading,
    clearErrors,
    errors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:          '',
            erpDriverType: ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            const { companyInfo, createErpAgent } = props;
            setSubmitting(true);
            const agentData = {
                name:      values.name,
                agentType: values.erpDriverType.name
            };

            let isCreated;
            clearErrors();

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createErpAgent(companyInfo.companyId, agentData);
                }

                if (isCreated) {
                    handleClose();
                    toggleCredentialsModal();
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            onRendered={formik.handleReset}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/erpAgentIconColor.svg" alt="settings icon" />
                        <span>New FSM Integration Settings</span>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="FSM Integration Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="FSM Driver Type"
                                name="erpDriverType"
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                options={erpDriverTypes}
                                placeholder="FSM Driver Type"
                                value={formik.values.erpDriverType}
                                error={Boolean(formik.errors.erpDriverType) && Boolean(formik.touched.erpDriverType)}
                                helperText={Boolean(formik.touched.erpDriverType) && formik.errors.erpDriverType}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );

};

NewErpAgentModal.defaultProps = {
    toggleCredentialsModal: () => {}
};

NewErpAgentModal.propTypes = {
    close:                  func.isRequired,
    open:                   bool.isRequired,
    title:                  string.isRequired,
    toggleCredentialsModal: func.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ErpAgentActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(NewErpAgentModal);
